/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography, useTheme } from '@coursera/cds-core';

import RelatedSkillEntry from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/RelatedSkillEntry';
import { relatedSkills } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/relatedSkillsData';
import type { ExactlyOne } from 'bundles/program-skills-objectives/types';

import _t from 'i18n!nls/program-skills-objectives';

const useStyles = () => {
  const { palette, spacing } = useTheme();

  return {
    container: css({
      width: '100%',
      background: palette.gray[100],
      padding: spacing(24),
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(16),
    }),
    skillsContainer: css({
      display: 'flex',
      flexWrap: 'wrap',
      gap: spacing(24),
    }),
  };
};

type Props = ExactlyOne<{
  programSlug: string;
  orgSlug: string;
}> & {
  skillName: string;
  skillSetSlug?: string;
  currentSkillId: string;
};

export const RelatedSkillsBanner = ({
  programSlug,
  orgSlug,
  skillSetSlug,
  currentSkillId,
  skillName,
}: Props): React.ReactElement => {
  const styles = useStyles();

  const relevantSkills = relatedSkills[currentSkillId as keyof typeof relatedSkills];
  if (!relevantSkills) return <></>;

  const baseUrl = orgSlug ? `/organizations/${orgSlug}` : `/programs/${programSlug}`;

  return (
    <div css={styles.container}>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold" component="h2">
        {_t('Explore other popular skills related to #{skillName}', { skillName })}
      </Typography>
      <div css={styles.skillsContainer}>
        {relevantSkills.map((skillId) => (
          <RelatedSkillEntry
            key={skillId}
            skillId={skillId}
            baseUrl={baseUrl}
            trackingData={{
              ...(orgSlug ? { orgSlug } : { programSlug }),
              skillSetSlug,
              currentSkillId,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default RelatedSkillsBanner;
